'use client'

import {cx, tv} from '@local/utils/cx'
import {type ReactNode, Children, useRef, useEffect, useState, createRef, RefObject, useCallback, cloneElement, useImperativeHandle, forwardRef, ForwardedRef} from 'react'
import {observe} from 'react-intersection-observer'
import {DotNavigation} from './DotNavigation'
import {PrevNextNavigation} from './PrevNextNavigation'
import {useResizeObserver} from '@local/hooks'
import {ButtonProps} from '@local/ui/components/button/Button'

export interface SlideshowNativeProps {
	children: ReactNode
	className?: string
	containerClassName?: string
	disableLg?: boolean
	withDotNavigation?: boolean
	withPrevNextNavigation?: boolean
	dotNavigationClassName?: string,
	prevNextNavigationClassName?: string,
	prevNextButtonClassName?: string
	prevNextButtonVariant?: ButtonProps['variant']
	loop?: boolean
	autoPlayInterval?: number
	threshold?: number
	disableObserver?: boolean
	disableObserverOnMobile?: boolean
	containerRef?: RefObject<HTMLDivElement>
	elCount?: number
	onChange?: (index: number) => void
	onScroll?: (e) => void
	button?: ReactNode
}

export const SlideshowNative = forwardRef(
	({
		children,
		className,
		containerClassName,
		disableLg = false,
		withDotNavigation = false,
		withPrevNextNavigation = false,
		dotNavigationClassName,
		prevNextNavigationClassName,
		prevNextButtonClassName,
		prevNextButtonVariant = 'icon',
		loop = false,
		autoPlayInterval,
		onChange,
		threshold = 0.4,
		disableObserver = false,
		disableObserverOnMobile = false,
		onScroll,
		elCount,
		button,
		containerRef,
	}: SlideshowNativeProps,
	parentRef: ForwardedRef<HTMLDivElement>,
	) => {
		const ref = useRef<HTMLElement>()
		const setRefs = useCallback((node) => {
			ref.current = node
			if (parentRef) parentRef.current = node
		}, [])

		const timeout = useRef<number>()
		const autoPlayStopped = useRef<boolean>(false)
		const hasTouched = useRef<boolean>(false)
		const [index, setIndex] = useState<number>(0)
		const indexRef = useRef<number>(0)
		const count = useRef<number>(0)
		const childrenRef = useRef<RefObject<HTMLElement>[]>([])
		const offsets = useRef<number[]>([])

		// register refs for each child
		count.current = 0
		Children.forEach(children, (child, i) => {
			if (elCount) {
				count.current = elCount
				return
			}
			if (child && !child.props?.className?.match(/\b\w*hidden\w*\b/g)) {
				childrenRef.current[i] = child.ref || childrenRef.current[i] || createRef()
				count.current++
			}
		})

		const container = tv({
			base: ['relative flex [&>*]:shrink-0', className],
			variants: {
				disableLg: {
					true: [
						'lg-max:justify-start', 'lg-max:items-stretch', 'lg-max:relative', 'lg-max:[&>*]:snap-start',
						'lg-max:overflow-auto',
						'lg-max:snap-x', 'lg-max:snap-mandatory', 'lg-max:scroll-smooth',
					],
					false: [
						'justify-start', 'items-stretch', 'relative', '[&>*]:snap-start',
						'overflow-auto', 'overscroll-x-contain', 'scrollbar-hide',
						'snap-x', 'snap-mandatory', 'scroll-smooth',
					],
				},
			},
		}, {
			responsiveVariants: ['lg-max'],
		})

		const stopAutoPlay = () => {
			clearTimeout(timeout.current)
			autoPlayStopped.current = true
		}

		const handleChildEnter = (i, inView) => {
			if (disableObserver) return
			if (disableObserverOnMobile) {
				if (inView && hasTouched.current) {
					setIndex(i)
					onChange?.(i)
				}
			} else if (inView) {
				setIndex(i)
				onChange?.(i)
			}
		}

		const scrollTo = (i: number) => {
			hasTouched.current = false
			indexRef.current = i
			setIndex(indexRef.current)
			onChange?.(i)

			if (ref.current) {
				ref.current.scrollTo({left: offsets.current[i], behavior: 'smooth'})
			}
		}

		const handleTouchStart = () => {
			stopAutoPlay()
			hasTouched.current = true
		}

		const handlePrev = () => {
			stopAutoPlay()
			const nextIndex = indexRef.current >= 1
				? indexRef.current - 1
				: loop ? indexRef.current + count.current - 1 : 0
			scrollTo(nextIndex)
		}

		const handleNext = useCallback((forceLoop = false) => {
			if (!forceLoop) stopAutoPlay()
			const nextIndex = indexRef.current < count.current - 1
				? indexRef.current + 1
				: loop || forceLoop ? (indexRef.current + 1) % count.current : count.current - 1
			scrollTo(nextIndex)
		}, [count.current])

		const handleNavChange = (i) => {
			stopAutoPlay()
			scrollTo(i)
		}

		useEffect(() => {
			if (autoPlayInterval && !autoPlayStopped.current) {
				timeout.current = window.setTimeout(() => {
					handleNext(true)
				}, autoPlayInterval)
			}

			return () => {
				clearTimeout(timeout.current)
			}
		}, [index])

		useEffect(() => {
			const unobserveCallbacks = childrenRef.current?.map((child, i) => {
				if (child.current === null) return undefined
				return child ? observe(child as unknown as Element, (inView) => handleChildEnter(i, inView), {threshold}) : undefined
			})

			return () => {
				unobserveCallbacks?.forEach(unobserve => unobserve?.())
			}
		}, [children])

		useResizeObserver(ref, () => {
			childrenRef.current.forEach((_, i) => {
				offsets.current[i] = childrenRef.current[i]?.offsetLeft
			})
		}, [childrenRef.current])

		useImperativeHandle(parentRef, () => {
			return {
				node: ref.current,
				scrollToIndex: scrollTo,
				handleNext,
				handlePrev,
			}
		}, [])

		return (
			<div className={containerClassName} ref={containerRef}>
				<div
					ref={setRefs}
					className={container({
						disableLg: {
							initial: disableLg,
						},
					})}
					onScroll={onScroll}
					onTouchStart={handleTouchStart}>
					{Children.map(children, (child, _index) =>
						cloneElement(child, {
							ref: (ref) => (childrenRef.current[_index] = ref),
						}),
					)}
				</div>
				{withDotNavigation && (
					<DotNavigation
						onChange={handleNavChange}
						index={index}
						count={count.current}
						className={cx(disableLg && 'lg:hidden', dotNavigationClassName)} />
				)}
				<div className='flex items-center justify-end gap-20'>

					{withPrevNextNavigation && (
						<PrevNextNavigation
							onPrev={handlePrev}
							onNext={handleNext}
							loop={loop}
							buttonState={index === 0 ? -1 : index >= count.current - 1 ? 1 : 0}
							buttonClassName={prevNextButtonClassName}
							buttonVariant={prevNextButtonVariant}
							className={cx(disableLg && 'lg:hidden', prevNextNavigationClassName)} />
					)}
					{!!button && button}
				</div>
			</div>
		)
	},
)
